import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import HelmetWrapper from 'components/HelmetWrapper';
import classNames from 'classnames';
import Subnav from 'components/Subnav';
import HomeIcon from 'icons/Home.icon';
import ReportsIcon from 'icons/Reports.icon';
import TableIcon from 'icons/Table.icon';
import { Button } from 'ncoded-component-library';
import { downloadUrlInNewWindow, getDataRoomRoute } from 'utils';
import useQueryParams from 'hooks/useQueryParams';
import dataRoomApi from '../../dataRoomApi';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import downloadService from 'services/downloadService';
import FileIcon from 'icons/File.icon';
import OnboardingContext from '../../providers/Onboarding/Onboarding.context';
import ExportAnalyticsButton from '../../components/ExportAnalyticsButton';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

import './Reports.styles.scss';

const ReportsRouter: React.FC = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    doesntHaveAccess,
    hasIndexGenerated,
    isAdmin,
    dataRoom: { _id, name },
  } = useContext(DataRoomContext);
  const { contentCard } = useContext(OnboardingContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const { startReportDownload, failReportDownload } = useContext(
    DownloadStatusContext,
  );

  const {
    params: { action, dataRoomMemberId, from, to },
  } = useQueryParams();

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const isOnOverview = useLocation().pathname.includes('overview');

  const classes = classNames('reports', 'page');

  const links = useMemo(
    () => [
      {
        to: 'overview',
        label: t('overview'),
        Icon: HomeIcon,
      },
      {
        to: 'activity-log',
        label: t('activityLog'),
        Icon: ReportsIcon,
      },
    ],
    [t],
  );

  const params = useMemo(
    () => ({
      action,
      dataRoomMemberId,
      createdAt: from || to ? { $gte: from, $lte: to } : null,
    }),
    [action, dataRoomMemberId, from, to],
  );

  const downloadIndexFile = useCallback(async () => {
    try {
      setIsDownloading(true);

      const {
        data: { url, type },
      } = await dataRoomApi.room.getExportedIndex(_id);

      if (url) downloadUrlInNewWindow(url, type);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDownloading(false);
    }
  }, [_id, popServerError]);

  const downloadPDF = useCallback(async () => {
    try {
      startReportDownload(true);

      await dataRoomApi.reports.getPdfReport(_id, {
        ...params,
        uniqueId: downloadService.downloadUniqueId,
      });
    } catch (e) {
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
      );
      failReportDownload();
    }
  }, [_id, params, failReportDownload, startReportDownload, getMixpanelProps]);

  const downloadCSV = useCallback(async () => {
    try {
      startReportDownload();

      await dataRoomApi.reports.getCsvReport(_id, {
        ...params,
        uniqueId: downloadService.downloadUniqueId,
      });
    } catch (e) {
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
      );
      failReportDownload();
    }
  }, [_id, params, failReportDownload, startReportDownload, getMixpanelProps]);

  const RenderExportIndex = useMemo(
    () =>
      isOnOverview ? (
        <>
          <ExportAnalyticsButton />

          {hasIndexGenerated && (
            <Button
              icon={FileIcon}
              onClick={downloadIndexFile}
              disabled={isDownloading}
            >
              {t('fileIndex')}
            </Button>
          )}
        </>
      ) : (
        <>
          <p>{t('export')}</p>
          <Button icon={<ReportsIcon strokeWidth={2} />} onClick={downloadPDF}>
            {t('logPdf')}
          </Button>
          <Button icon={TableIcon} onClick={downloadCSV}>
            {t('logCsv')}
          </Button>
        </>
      ),
    [
      hasIndexGenerated,
      isDownloading,
      isOnOverview,
      downloadCSV,
      downloadIndexFile,
      downloadPDF,
      t,
    ],
  );

  if (!isAdmin)
    return <Navigate to={getDataRoomRoute(_id, 'documents')} replace />;

  return (
    <div className={classes}>
      <HelmetWrapper title={`${t('analytics&Insights')} - ${name}`} />
      <h2>{t('analytics&Insights')}</h2>
      {contentCard}
      <div className="reports__heading">
        <Subnav links={links} />
        {!doesntHaveAccess && <div>{RenderExportIndex}</div>}
      </div>
      <Outlet />
    </div>
  );
};

export default ReportsRouter;
